import { BackButton, PageContainer } from "@client/components/ui";
import { Badge } from "@client/components/ui/badge";
import { Button } from "@client/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@client/components/ui/card";
import { Skeleton } from "@client/components/ui/skeleton";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@client/components/ui/tooltip";
import { CONTACT_URL } from "@client/lib/site-links";
import { trpc } from "@client/lib/trpc-client";
import { formatDate } from "@client/lib/utils";
import { isBrazil } from "@shared/lib/countries";
import { createFileRoute } from "@tanstack/react-router";
import {
	CheckCircle,
	ChevronLeft,
	ChevronRight,
	Download,
	FileText,
	Loader2,
	Receipt,
	RefreshCw,
} from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/(protected)/settings/invoices")({
	component: InvoicesPage,
});

type InvoiceStatus =
	| "processing"
	| "authorized"
	| "cancelled"
	| "error"
	| "disputed"
	| "cancelled_dispute"
	| "dispute_cancel_failed";

function InvoicesPage() {
	const [page, setPage] = useState(0);
	const limit = 10;

	const historyQuery = trpc.nfse.getHistory.useQuery({
		limit,
		offset: page * limit,
	});

	const countQuery = trpc.nfse.getCount.useQuery();
	const summaryQuery = trpc.nfse.getSummary.useQuery();

	const totalPages = Math.ceil((countQuery.data ?? 0) / limit);
	const hasNextPage = page < totalPages - 1;
	const hasPrevPage = page > 0;

	const getStatusConfig = (status: InvoiceStatus) => {
		switch (status) {
			case "authorized":
				return {
					label: "Issued",
					variant: "default" as const,
					className:
						"bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
					tooltip: "Invoice/NFS-e issued successfully",
				};
			case "processing":
				return {
					label: "Generating",
					variant: "outline" as const,
					className:
						"bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
					tooltip: "Invoice/NFS-e is being generated",
				};
			case "error":
				return {
					label: "Invoice Error",
					variant: "destructive" as const,
					className: "",
					tooltip: "Invoice/NFS-e generation failed - payment was successful",
				};
			case "cancelled":
			case "cancelled_dispute":
				return {
					label: "Cancelled",
					variant: "secondary" as const,
					className: "",
					tooltip: "Invoice/NFS-e was cancelled",
				};
			case "disputed":
			case "dispute_cancel_failed":
				return {
					label: "Disputed",
					variant: "outline" as const,
					className:
						"bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
					tooltip: "Invoice/NFS-e is under dispute",
				};
			default:
				return {
					label: "Unknown",
					variant: "secondary" as const,
					className: "",
					tooltip: "",
				};
		}
	};

	const getTransactionTypeLabel = (type: string) => {
		switch (type) {
			case "subscription":
				return "Subscription";
			case "credit_purchase":
				return "Credits";
			default:
				return type;
		}
	};

	const formatCurrencyBrl = (value: number | null) => {
		if (value === null || value === undefined) return "--";

		return new Intl.NumberFormat("pt-BR", {
			style: "currency",
			currency: "BRL",
		}).format(value);
	};

	const formatCurrencyUsd = (value: number | null) => {
		if (value === null || value === undefined) return "--";

		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
		}).format(value);
	};

	return (
		<PageContainer maxWidth="narrow">
			<BackButton to="/settings" label="Back to Settings" />

			{/* Header */}
			<div className="mb-8">
				<div className="flex items-center gap-3">
					<Receipt className="h-8 w-8 text-primary" />
					<h1 className="font-semibold text-3xl tracking-tight">Invoices</h1>
				</div>
				<p className="mt-1 text-muted-foreground">
					View and download your payment invoices
				</p>
			</div>

			{/* Summary Cards */}
			{summaryQuery.data && summaryQuery.data.total > 0 && (
				<div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
					<Card>
						<CardContent className="pt-6">
							<div className="text-muted-foreground text-sm">Total Spent</div>
							<div className="font-semibold text-2xl text-primary">
								{formatCurrencyUsd(summaryQuery.data.totalValueUsd)}
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-6">
							<div className="text-muted-foreground text-sm">
								Total Invoices
							</div>
							<div className="font-semibold text-2xl">
								{summaryQuery.data.total}
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-6">
							<div className="text-muted-foreground text-sm">Subscriptions</div>
							<div className="font-semibold text-2xl">
								{summaryQuery.data.subscriptionCount}
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-6">
							<div className="text-muted-foreground text-sm">
								Credit Purchases
							</div>
							<div className="font-semibold text-2xl">
								{summaryQuery.data.creditPurchaseCount}
							</div>
						</CardContent>
					</Card>
				</div>
			)}

			{/* Invoice List */}
			<Card>
				<CardHeader className="pb-3">
					<div className="flex items-center justify-between">
						<div>
							<CardTitle className="text-lg">Invoice History</CardTitle>
							<CardDescription>
								Download invoices for your records
							</CardDescription>
						</div>
						<Button
							variant="outline"
							size="sm"
							onClick={() => historyQuery.refetch()}
							disabled={historyQuery.isRefetching}
						>
							{historyQuery.isRefetching ? (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							) : (
								<RefreshCw className="mr-2 h-4 w-4" />
							)}
							Refresh
						</Button>
					</div>
				</CardHeader>

				<CardContent>
					{historyQuery.isLoading ? (
						<div className="space-y-3">
							{Array.from({ length: 5 }).map((_, i) => (
								<Skeleton key={i} className="h-24 w-full rounded-lg" />
							))}
						</div>
					) : historyQuery.data?.length === 0 ? (
						<div className="flex h-40 flex-col items-center justify-center rounded-lg border border-border border-dashed text-center">
							<Receipt className="mb-3 h-10 w-10 text-muted-foreground/50" />
							<p className="font-medium text-muted-foreground">
								No invoices yet
							</p>
							<p className="mt-1 text-muted-foreground text-sm">
								Invoices will appear here after your first payment
							</p>
						</div>
					) : (
						<div className="space-y-2">
							{historyQuery.data?.map((invoice) => {
								const statusConfig = getStatusConfig(
									invoice.status as InvoiceStatus,
								);
								const invoiceCountry = invoice.customerCountry;
								const isInvoiceBrazilian = invoiceCountry
									? isBrazil(invoiceCountry)
									: false;

								// One document per customer type. Brazilian customers get the
								// NFS-e, the legally meaningful document for them. Everyone
								// else gets the commercial invoice, in the currency they were
								// charged — an NFS-e is still emitted for them, but it is a
								// Portuguese fiscal document that means nothing abroad.
								// Serve from our own R2 mirror. Fiscal Nacional's `pdf_url`
								// points at its dashboard, which needs an account there — our
								// customers would just hit a login loop. `pdfUrl`/`xmlUrl` hold
								// the R2 keys once mirrored; the route 202s until then.
								const nfseDownloadUrl = invoice.pdfUrl
									? `/api/v1/nfse/${invoice.id}/pdf`
									: null;
								const invoiceDownloadUrl =
									invoice.xmlUrl || invoice.invoiceUrl
										? `/api/v1/nfse/${invoice.id}/invoice`
										: null;
								const showNfse = isInvoiceBrazilian;
								const showInvoice = !isInvoiceBrazilian && !!invoiceDownloadUrl;
								// Authorized in Joinville, but the prefecture PDF has not been
								// mirrored yet — tell the customer instead of showing nothing.
								const nfsePending =
									showNfse &&
									invoice.status === "authorized" &&
									!nfseDownloadUrl;

								return (
									<div
										key={invoice.id}
										className="flex items-center justify-between gap-4 rounded-lg border border-border bg-card/50 px-4 py-3 transition-colors hover:bg-accent/30"
									>
										{/* Left side - Info */}
										<div className="flex min-w-0 flex-1 items-center gap-4">
											{/* Product name */}
											<div className="min-w-0 flex-1">
												<div className="flex items-center gap-2">
													<span className="truncate font-medium">
														{invoice.productName || invoice.serviceDescription}
													</span>
													<span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-muted-foreground text-xs">
														{getTransactionTypeLabel(invoice.transactionType)}
													</span>
												</div>
												<div className="mt-0.5 flex items-center gap-2 text-muted-foreground text-sm">
													<span>{formatDate(invoice.createdAt)}</span>
													{invoice.errorMessage && (
														<span className="text-destructive">• Error</span>
													)}
												</div>
											</div>
										</div>

										{/* Center - Status badges */}
										<div className="hidden items-center gap-2 sm:flex">
											<TooltipProvider>
												<Tooltip>
													<TooltipTrigger asChild>
														<Badge
															variant="default"
															className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
														>
															<CheckCircle className="mr-1 h-3 w-3" />
															Paid
														</Badge>
													</TooltipTrigger>
													<TooltipContent>
														<p>Payment completed via Stripe</p>
													</TooltipContent>
												</Tooltip>
											</TooltipProvider>

											<TooltipProvider>
												<Tooltip>
													<TooltipTrigger asChild>
														<Badge
															variant={statusConfig.variant}
															className={statusConfig.className}
														>
															{statusConfig.label}
														</Badge>
													</TooltipTrigger>
													<TooltipContent>
														<p>{statusConfig.tooltip}</p>
													</TooltipContent>
												</Tooltip>
											</TooltipProvider>
										</div>

										{/* Right side - Amount and actions */}
										<div className="flex shrink-0 items-center gap-4">
											<span className="font-semibold">
												{invoice.serviceValueUsd
													? formatCurrencyUsd(invoice.serviceValueUsd)
													: formatCurrencyBrl(invoice.serviceValueBrl)}
											</span>

											<div className="flex gap-1">
												{/* Commercial invoice — international customers only */}
												{showInvoice && (
													<Button variant="ghost" size="icon" asChild>
														<a
															href={invoiceDownloadUrl ?? "#"}
															target="_blank"
															rel="noopener noreferrer"
															title="Download Invoice"
														>
															<Download className="h-4 w-4" />
														</a>
													</Button>
												)}

												{/* NFS-e — Brazilian customers only */}
												{showNfse &&
													invoice.status === "authorized" &&
													nfseDownloadUrl && (
														<Button variant="ghost" size="icon" asChild>
															<a
																href={nfseDownloadUrl}
																target="_blank"
																rel="noopener noreferrer"
																title={`NFS-e${invoice.numero ? ` #${invoice.numero}` : ""}`}
															>
																<FileText className="h-4 w-4" />
															</a>
														</Button>
													)}

												{/* Authorized, but the prefecture PDF is not mirrored
													yet — say so instead of showing nothing. */}
												{nfsePending && (
													<TooltipProvider>
														<Tooltip>
															<TooltipTrigger asChild>
																<Button
																	variant="ghost"
																	size="icon"
																	aria-disabled="true"
																	className="cursor-default opacity-40"
																	onClick={(e) => e.preventDefault()}
																	aria-label="NFS-e is being issued"
																>
																	<FileText className="h-4 w-4" />
																</Button>
															</TooltipTrigger>
															<TooltipContent>
																<p>
																	NFS-e
																	{invoice.numero ? ` #${invoice.numero}` : ""}{" "}
																	authorized — the PDF becomes available
																	shortly.
																</p>
															</TooltipContent>
														</Tooltip>
													</TooltipProvider>
												)}
											</div>

											{invoice.status === "processing" && (
												<Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
											)}
										</div>
									</div>
								);
							})}
						</div>
					)}

					{/* Pagination */}
					{totalPages > 1 && (
						<div className="mt-6 flex items-center justify-between border-border border-t pt-4">
							<span className="text-muted-foreground text-sm">
								Page {page + 1} of {totalPages}
							</span>
							<div className="flex gap-2">
								<Button
									variant="outline"
									size="sm"
									onClick={() => setPage((p) => p - 1)}
									disabled={!hasPrevPage || historyQuery.isFetching}
								>
									<ChevronLeft className="mr-1 h-4 w-4" />
									Previous
								</Button>
								<Button
									variant="outline"
									size="sm"
									onClick={() => setPage((p) => p + 1)}
									disabled={!hasNextPage || historyQuery.isFetching}
								>
									Next
									<ChevronRight className="ml-1 h-4 w-4" />
								</Button>
							</div>
						</div>
					)}
				</CardContent>
			</Card>

			{/* Help text */}
			<p className="mt-6 text-center text-muted-foreground text-sm">
				Need help with billing?{" "}
				<a
					href={CONTACT_URL}
					target="_blank"
					rel="noopener noreferrer"
					className="text-primary underline-offset-4 hover:underline"
				>
					Contact support
				</a>
			</p>
		</PageContainer>
	);
}
